/* eslint-disable no-console */
import { StoragePriority, QuotaInfo } from "./StoragePriority";
import { getPriorityForKey } from "./StorageConfig";
import { QuotaMonitor } from "./QuotaMonitor";

/**
 * Singleton class to manage localStorage with priority-based quota protection.
 */
export class StorageManager {
  private static instance: StorageManager;
  private isDev = process.env.NODE_ENV === "development";
  private isProcessing = false;

  private constructor() {}

  public static getInstance(): StorageManager {
    if (!StorageManager.instance) {
      StorageManager.instance = new StorageManager();
    }
    return StorageManager.instance;
  }

  /**
   * Safe version of localStorage.setItem with quota protection and auto-cleanup.
   */
  public setItem(
    key: string,
    value: string,
    priority?: StoragePriority,
    silent: boolean = false
  ): boolean {
    if (typeof window === "undefined") return false;
    if (this.isProcessing) return false;

    this.isProcessing = true;
    const itemPriority = priority ?? getPriorityForKey(key);

    try {
      localStorage.setItem(key, value);
      this.isProcessing = false;
      return true;
    } catch (error) {
      if (this.isQuotaExceededError(error)) {
        if (this.isDev && !silent) {
          console.warn(
            `[StorageManager] Quota exceeded for key: ${key}. Attempting cleanup...`
          );
        }

        const success = this.cleanupAndRetry(key, value, itemPriority, silent);
        if (!success && this.isDev && !silent) {
          console.error(
            `[StorageManager] Failed to save key: ${key} even after cleanup.`
          );
        }
        this.isProcessing = false;
        return success;
      }
      this.isProcessing = false;
      return false;
    }
  }

  /**
   * Safe version of localStorage.getItem.
   */
  public getItem(key: string): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(key);
  }

  /**
   * Safe version of localStorage.removeItem.
   */
  public removeItem(key: string): void {
    if (typeof window === "undefined") return;
    localStorage.removeItem(key);
  }

  /**
   * Get quota information.
   */
  public getQuotaInfo(): QuotaInfo {
    return QuotaMonitor.getQuotaInfo();
  }

  /**
   * Identifies if an error is a localStorage quota error.
   */
  private isQuotaExceededError(error: any): boolean {
    return (
      error instanceof DOMException &&
      (error.code === 22 ||
        error.code === 1014 ||
        error.name === "QuotaExceededError" ||
        error.name === "NS_ERROR_DOM_QUOTA_REACHED")
    );
  }

  /**
   * Attempts to free up space based on priorities and retries saving.
   */
  private cleanupAndRetry(
    key: string,
    value: string,
    priority: StoragePriority,
    silent: boolean
  ): boolean {
    // Priority cleanup logic:
    // 1. Delete LOW if priority is > LOW (wait, LOW is 3, higher number = lower priority)
    // Actually our enum is: CRITICAL=0, IMPORTANT=1, NORMAL=2, LOW=3

    // Cleanup sequence:
    // If priority is CRITICAL(0): cleanup LOW, then NORMAL, then IMPORTANT
    // If priority is IMPORTANT(1): cleanup LOW, then NORMAL
    // If priority is NORMAL(2): cleanup LOW
    // If priority is LOW(3): cleanup nothing (except itself maybe, but we'll try to save it)

    const prioritiesToCleanup = [
      StoragePriority.LOW,
      StoragePriority.NORMAL,
      StoragePriority.IMPORTANT,
    ];

    for (const p of prioritiesToCleanup) {
      if (priority < p) {
        // Target priority is more important than p
        this.cleanupByPriority(p);

        try {
          localStorage.setItem(key, value);
          if (this.isDev && !silent) {
            console.log(
              `[StorageManager] Successfully saved ${key} after cleaning priority ${StoragePriority[p]}`
            );
          }
          return true;
        } catch (e) {
          // Still full, continue to next priority
        }
      }
    }

    // Last resort for CRITICAL: clear everything except other CRITICAL items
    if (priority === StoragePriority.CRITICAL) {
      this.cleanupByPriority(StoragePriority.IMPORTANT);
      try {
        localStorage.setItem(key, value);
        return true;
      } catch (e) {
        return false;
      }
    }

    return false;
  }

  /**
   * Removes all items from localStorage that match a specific priority.
   */
  private cleanupByPriority(priority: StoragePriority): void {
    const keysToRemove: string[] = [];

    for (const key in localStorage) {
      if (Object.prototype.hasOwnProperty.call(localStorage, key)) {
        if (getPriorityForKey(key) === priority) {
          keysToRemove.push(key);
        }
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }
}
